import React from 'react';
import { UserDataQuery, formatIsoDate } from '@front-activerse/common-components';
import {
  CookbookCategoriesConnectorApollo,
  AddFavoriteCookbookMealConnectorApollo,
  RemoveFavoriteCookbookMealConnectorApollo,
  FetchNotificationConnectorRemoteConfig,
  FetchClosedNotificationsConnectorFirestoreDb,
  useTranslator,
} from '@front-activerse/common-components';
import { useApolloClient } from '@apollo/client';

import CookbookIndexContainer from './CookbookIndexContainer';

interface ICookbookIndexPage {
  cookbookNavigation: {
    path: string;
    translationKey: string;
  }[];
  categoryAllCoverImageUrl: string;
  categoryAllName: string;
  fetchRemoteConfigFields: (
    fields: string[]
  ) => Promise<{ [key: string]: string | number | boolean }>;
  getClosedNotifications: (collectionName: string, userId: number) => Promise<number[]>;
  markNotificationAsClosed: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
}

export const CookbookIndexPage: React.FC<ICookbookIndexPage> = ({
  cookbookNavigation,
  categoryAllCoverImageUrl,
  categoryAllName,
  fetchRemoteConfigFields,
  getClosedNotifications,
  markNotificationAsClosed,
}) => {
  const client = useApolloClient();
  const { language } = useTranslator();

  const userData = client.readQuery({
    query: UserDataQuery,
    variables: { date: formatIsoDate(new Date()) },
  });

  const userId = userData?.me?.id || 0;

  const remoteConfigLanguageCode = language.substring(0, 2);

  return (
    <FetchClosedNotificationsConnectorFirestoreDb
      getClosedNotifications={getClosedNotifications}
      userId={userId}
      collectionName="cookbook_notifications"
    >
      {({ ...fetchClosedNotificationsData }) => (
        <FetchNotificationConnectorRemoteConfig
          fetchRemoteConfigFields={fetchRemoteConfigFields}
          remoteConfigFields={[
            'cookbook_notification_active',
            'cookbook_notification_id',
            'cookbook_notification_img',
            'cookbook_notification_content',
            'cookbook_notification_close_btn_text',
            'cookbook_notification_redirect_btn_text',
            'cookbook_notification_redirect_url_path',
          ]}
          remoteConfigLanguageCode={remoteConfigLanguageCode}
        >
          {({ ...fetchCookbookNotificationData }) => (
            <CookbookCategoriesConnectorApollo>
              {(cookbookCategoriesData) => (
                <AddFavoriteCookbookMealConnectorApollo>
                  {({ addCookbookMealToFavorite }) => (
                    <RemoveFavoriteCookbookMealConnectorApollo>
                      {({ removeCookbookMealFromFavorites }) => (
                        <CookbookIndexContainer
                          {...cookbookCategoriesData}
                          addToFavorites={addCookbookMealToFavorite}
                          removeFromFavorites={removeCookbookMealFromFavorites}
                          cookbookNavigation={cookbookNavigation}
                          categoryAllCoverImageUrl={categoryAllCoverImageUrl}
                          categoryAllName={categoryAllName}
                          {...fetchCookbookNotificationData}
                          {...fetchClosedNotificationsData}
                          loadingMealPlanNotification={
                            fetchCookbookNotificationData.loading ||
                            fetchClosedNotificationsData.loading
                          }
                          loading={cookbookCategoriesData.loading}
                          markNotificationAsClosed={markNotificationAsClosed}
                          userId={userId}
                        />
                      )}
                    </RemoveFavoriteCookbookMealConnectorApollo>
                  )}
                </AddFavoriteCookbookMealConnectorApollo>
              )}
            </CookbookCategoriesConnectorApollo>
          )}
        </FetchNotificationConnectorRemoteConfig>
      )}
    </FetchClosedNotificationsConnectorFirestoreDb>
  );
};

export default CookbookIndexPage;
